'use client';

import type { FC } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { useStore } from '../../../hooks/use-store';
import { toastsStore } from '../../../stores/toasts-store';
import { Toast } from '../Toast';

/**
 * A toaster which displays toasts.
 *
 * The toaster uses custom state management, there is **no need** to wrap the toaster in a client component or manage Context Providers.
 *
 * ## Usage
 *
 * Add the Toaster somewhere where you want to render the toasts.
 *
 * ```tsx
 * import { Toaster } from '@tuic/web-compositions/shared/Toaster';
 *
 * <Toaster />
 * ```
 *
 * Push a toast from anywhere to the toaster -- see {@link useToasts} for more detailed usage.
 *
 * ```tsx
 * import { useToasts } from '@tuic/web-compositions/shared/Toaster';
 *
 * const { push } = useToasts();
 *
 * push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 * });
 * ```
 *
 * ## Duration
 *
 * The default duration is set to 3 seconds. You can customize it for each toast individually by providing a `duration` property to the `push()` handler.
 *
 * ⚠️ Do this only when advised so. Always strive to use the default duration.
 *
 * ```tsx
 * // This toast message disappears after 10 seconds.
 * push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 *   duration: 10000,
 * });
 * ```
 *
 * ## Removing a Pushed Message Manually
 *
 * You can remove a pushed message by invoking the return value of the `push()` function.
 *
 * ```tsx
 * // This toast message stays visible until removed.
 * const removeMessage = push({
 *   title: 'Your account has been created',
 *   text: 'Please verify your email address before you attempt to sign in.',
 *   duration: Infinity,
 * });
 *
 * // Let's remove it manually after 5 seconds.
 * // Don't do this for time based removals -- it's just for explanation purposes here.
 * setTimeout(() => {
 *   removeMessage();
 * }, 5000);
 * ```
 */
export const Toaster: FC = () => {
  const [toasts, setToasts] = useStore(toastsStore);
  const createOpenChangeHandler = (id: number) => {
    return (open: boolean) => {
      if (open) {
        return;
      }
      setToasts(prev => prev.filter(toast => id !== toast.id));
    };
  };
  return <div className="pointer-events-none mx-auto max-w-[585px]" data-sentry-component="Toaster" data-sentry-source-file="Toaster.tsx">
      <RadixToast.Provider data-sentry-element="RadixToast.Provider" data-sentry-source-file="Toaster.tsx">
        {toasts.map(toast => <RadixToast.Root key={toast.id} duration={toast.duration} onOpenChange={createOpenChangeHandler(toast.id)} defaultOpen className="pointer-events-auto data-[state=closed]:animate-out data-[state=open]:animate-in" aria-live={'polite'}>
            <Toast title={toast.title}>{toast.text}</Toast>
          </RadixToast.Root>)}
        <RadixToast.Viewport className="space-y-8" data-sentry-element="RadixToast.Viewport" data-sentry-source-file="Toaster.tsx" />
      </RadixToast.Provider>
    </div>;
};